import React, { useState, useMemo, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';

export interface DropdownOption {
    label: string;
    value: string;
}

interface BreadcrumbDropdownProps {
    options: DropdownOption[];
    customSelected: DropdownOption | undefined;
}

const BreadcrumbDropdown: React.FC<BreadcrumbDropdownProps> = React.memo(
    ({ options, customSelected }) => {
        const [isOpen, setIsOpen] = useState(false);
        const location = useLocation();

        // Memoize the selected option to avoid unnecessary recalculations
        const selected = useMemo(() => {
            return customSelected
                ? customSelected
                : generateBreadcrumbSelectedOption(location.pathname);
        }, [location.pathname, customSelected]);

        // Memoize handleOptionClick to avoid unnecessary re-renders
        const handleOptionClick = useCallback(() => {
            setIsOpen(false); // Close the dropdown
        }, []);

        // Track if the dropdown was opened or closed manually
        const handleMouseEnter = useCallback(() => {
            if (!isOpen) {
                setIsOpen(true);
            }
        }, [isOpen]);

        const handleMouseLeave = useCallback(() => {
            if (isOpen) {
                setIsOpen(false);
            }
        }, [isOpen]);

        return (
            <StyledWrapper onMouseLeave={handleMouseLeave}>
                <LabelContainer onMouseEnter={handleMouseEnter}>
                    {selected.label} <ICONS.dropdownChevron />
                </LabelContainer>

                {isOpen && (
                    <DropdownContainer>
                        {options.map((option) => (
                            <OptionContainer
                                key={option.value}
                                onClick={() => handleOptionClick()}
                            >
                                <StyledNavLink
                                    to={option.value}
                                    activeStyle={{
                                        fontWeight: 'bold',
                                        color: aampeTheme.colors
                                            .textAccessibleWG01,
                                    }}
                                >
                                    {option.label}
                                </StyledNavLink>
                            </OptionContainer>
                        ))}
                    </DropdownContainer>
                )}
            </StyledWrapper>
        );
    },
);

export default BreadcrumbDropdown;

const StyledWrapper = styled.div({
    position: 'relative',
    display: 'inline-block',
    fontFamily: 'Inclusive Sans Variable, Arial, sans-serif',
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
});

const LabelContainer = styled.div({
    fontWeight: aampeTheme.weights.light,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
});

const DropdownContainer = styled.div({
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: 'white',
    border: '1px solid #ccc',
    borderRadius: '4px',
    zIndex: 1000,
    boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
    whiteSpace: 'nowrap',
});

const OptionContainer = styled.div({
    display: 'flex',
    pointerEvents: 'auto',
    '&:hover': {
        backgroundColor: aampeTheme.colors.backgroundWG01,
        cursor: 'pointer',
    },
});

const StyledNavLink = styled(NavLink)({
    textDecoration: 'none',
    color: aampeTheme.colors.textAccessibleWG01,
    width: '100%',
    height: '100%',
    padding: '8px',
});

export const generateBreadcrumbSelectedOption = (
    pathname: string,
): DropdownOption => {
    const segments = pathname.split('/').filter((segment) => segment !== '');

    let label =
        segments[1]?.charAt(0).toUpperCase() +
            segments[1]?.slice(1).replace('-', ' ') || '';

    const value = pathname;
    return { label: label, value: value };
};
